import { useMemo, useRef, useState } from 'react';
import './ColumnFinder.css';

export interface ColumnFinderProps {
  /** Every column caption in the grid, in display order. */
  columns: string[];
  /**
   * The element WRAPPING the grid. The finder looks for `.adg-scroll` inside it
   * and scrolls that horizontally, so it works with any <AppDataGrid>.
   */
  gridRef: React.RefObject<HTMLElement | null>;
  placeholder?: string;
}

/**
 * "Find column…" locator for very wide grids — type part of a column caption and
 * it scrolls that column into view. Extracted from Sample Registration's Sample
 * Login view (2026-09-23) so Sample Transfer's Sample Login can use the SAME
 * component rather than a second copy (CLAUDE.md rule 8 — never reinvent).
 *
 * Note this is NOT a row search: it filters COLUMN NAMES and jumps to the field.
 * A grid with ~49 columns cannot be navigated by horizontal scrolling alone.
 */
export function ColumnFinder({ columns, gridRef, placeholder }: ColumnFinderProps) {
  const [find, setFind] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<number | null>(null);

  const matches = useMemo(() => {
    const q = find.trim().toLowerCase();
    return q ? columns.filter(c => c.toLowerCase().includes(q)) : columns;
  }, [find, columns]);

  const jumpTo = (name: string) => {
    setOpen(false);
    setFind(name);
    const wrap = gridRef.current?.querySelector('.adg-scroll') as HTMLElement | null;
    if (!wrap) return;
    const heads = Array.from(wrap.querySelectorAll('th')) as HTMLElement[];
    const th = heads.find(h => (h.querySelector('.adg-th-label')?.textContent ?? '').trim() === name);
    if (!th) return;
    // Leave ~320px of context to the left so the column lands mid-viewport rather
    // than hard against the frozen edge.
    const target = wrap.scrollLeft + (th.getBoundingClientRect().left - wrap.getBoundingClientRect().left) - 320;
    wrap.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  };

  return (
    <div className="sr-colfind">
      <span aria-hidden="true">🔍</span>
      <input
        placeholder={placeholder ?? 'Find column… (e.g. Collector, PWSID, Free Chlorine)'}
        value={find}
        autoComplete="off"
        onChange={e => { setFind(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { blurTimer.current = window.setTimeout(() => setOpen(false), 120); }}
        onKeyDown={e => { if (e.key === 'Enter' && matches[0]) jumpTo(matches[0]); }}
      />
      <span className="sr-colfind-hint">of {columns.length} columns — jumps to the field</span>
      {open && (
        <div className="sr-colfind-menu">
          {matches.length === 0
            ? <div className="sr-colfind-item" style={{ color: '#9aa1ad' }}>No matching column.</div>
            : matches.map(c => (
              <button key={c} className="sr-colfind-item"
                onMouseDown={e => { e.preventDefault(); jumpTo(c); }}>{c}</button>
            ))}
        </div>
      )}
    </div>
  );
}
